import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import React, { ReactNode } from 'react';
import { Controller } from 'react-hook-form';
import { ensureArray } from '../../../utils/arrayUtils';
import FieldContainer from './FieldContainer';
import { FieldProps } from './FieldProps';
import useFormLabel from './useFormLabel';
import ValidationError from './ValidationError';

export interface SearchableSelectFieldProps<T> extends FieldProps<any> {
  options: T[];
  label?: string | undefined;
  helperText?: ReactNode;
  optional?: boolean;
}

// This component is similar the SearchableSelectField but allows the user to enter custom strings
// Since the freeSolo option of the MUI Autocomplete component is very picky about everything this
// is a new component that has a lot less features and only takes in arrays of values and does not
// support label and value getters.
export default function SearchableFreeSoloMultiSelectField<T>({
  control,
  name,
  options,
  helperText,
  rules,
  optional,
  ...props
}: SearchableSelectFieldProps<T>) {
  const label = useFormLabel(name, props.label, { optionalHint: optional });

  return (
    <FieldContainer>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <FormControl error={!!error} fullWidth>
            <Autocomplete
              options={options}
              renderValue={(rValue: readonly string[], getItemProps: any) =>
                rValue.map((option: string, index: number) => {
                  const { key, ...itemProps } = getItemProps({ index });
                  return (
                    <Chip
                      variant="filled"
                      label={option}
                      key={key}
                      {...itemProps}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label={label} />
              )}
              onChange={(_, data) => onChange(data)}
              value={ensureArray(value)}
              label={label}
              freeSolo
              multiple
              autoSelect
              filterSelectedOptions
              {...props}
            />

            {error && (
              <FormHelperText>
                <ValidationError error={error} />
              </FormHelperText>
            )}

            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        )}
        rules={rules}
      />
    </FieldContainer>
  );
}
